import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { useEffect, useRef } from 'react';

interface MotivatorDetails {
  name: string;
  definition: string;
  key_traits: string[];
  score: number;
  score_range: string;
  range_definition: string;
  range_key_traits: string[];
  // v2 score-band narrative fields
  what_this_tells_you?: string;
  in_the_role?: string;
  watch_out_for?: string;
  works_best_in?: string;
  work_manifestation: string[];
  execution_impact: {
    strengths: string[];
    challenges: string[];
  };
}

interface Expectation {
  details: MotivatorDetails;
  match: boolean;
}

interface CandidateResult {
  details: MotivatorDetails;
  match: boolean;
}

export interface WorkMotivatorData {
  score: number;
  expectations: Expectation[];
  candidate_result: CandidateResult[];
  version: number;
}

interface WorkMotivatorResponse {
  message: string;
  data: WorkMotivatorData;
}

/**
 * Hook for fetching work motivator data
 */
export const useWorkMotivator = (reportId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['workMotivator', reportId],
    queryFn: async () => {
      if (!reportId) {
        throw new Error('Report ID is required');
      }

      const response = await apiClient.get<WorkMotivatorResponse>(
        `/reports/${reportId}/work-motivator/`
      );
      return response.data.data;
    },
    enabled: !!reportId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch work motivator';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    workMotivator: data,
    isLoading,
    error,
    refetch,
  };
};
